import { useEffect, useMemo, useState } from 'react';
import type { ViewResolution } from '@ayumia/contracts';
import type { ApiClient } from '../api/client';
import { avatarTone } from '../design-system/primitives';
import { text } from '../app/i18n';
import { formatValue, type FieldSummary, type ObjectSummary } from '../views/format';
import { humanize, type WireEdge } from './graph-model';

/**
 * Lectures de l'explorateur (ADR-129 §1) : toutes sous l'identité courante, par l'API v1, sans moteur.
 * - le résumé d'un type (libellés, champs, relations déclarées) par `GetView`, sous le droit de lire ce type ;
 * - le détail d'un nœud (code, statut, attributs) par l'Object API, pour les seuls nœuds visibles.
 * Un refus rend `null` : le nœud reste tel que la traversée l'a rendu, sans détail inventé.
 */

/** Résumés de type, par client d'API : la session et le tenant de ce client, jamais un autre (ERP-CACHE-001). */
const SUMMARIES = new WeakMap<ApiClient, Map<string, Promise<ObjectSummary | null>>>();

export function typeSummary(client: ApiClient, objectType: string): Promise<ObjectSummary | null> {
  let cache = SUMMARIES.get(client);
  if (cache === undefined) SUMMARIES.set(client, (cache = new Map()));
  let found = cache.get(objectType);
  if (found === undefined) {
    found = client.query<ViewResolution>('GetView', { object_type: objectType }).then(
      (response) => response.data.object ?? null,
      () => null,
    );
    cache.set(objectType, found);
  }
  return found;
}

/** Résumés des types rencontrés, chargés une fois chacun. */
export function useTypeSummaries(client: ApiClient, types: readonly string[]): ReadonlyMap<string, ObjectSummary | null> {
  const [summaries, setSummaries] = useState<ReadonlyMap<string, ObjectSummary | null>>(new Map());
  const wanted = useMemo(() => [...new Set(types)].sort().join(','), [types]);
  useEffect(() => {
    let live = true;
    const list = wanted === '' ? [] : wanted.split(',');
    void Promise.all(list.map(async (type) => [type, await typeSummary(client, type)] as const)).then((entries) => {
      if (live) setSummaries(new Map(entries));
    });
    return () => {
      live = false;
    };
  }, [client, wanted]);
  return summaries;
}

export type NodeRecord = Record<string, unknown> | null;

/** Détail des nœuds visibles, par lots de six lectures ; un refus vaut `null`. */
export function useNodeRecords(client: ApiClient, refs: ReadonlyArray<{ type: string; id: string }>): ReadonlyMap<string, NodeRecord> {
  const [records, setRecords] = useState<ReadonlyMap<string, NodeRecord>>(new Map());
  const wanted = useMemo(() => [...new Set(refs.map((ref) => `${ref.type}:${ref.id}`))].sort().join(','), [refs]);
  useEffect(() => {
    let live = true;
    const keys = wanted === '' ? [] : wanted.split(',');
    const missing = keys.filter((key) => !records.has(key));
    if (missing.length === 0) return;
    void (async () => {
      const found = new Map<string, NodeRecord>();
      for (let index = 0; index < missing.length; index += 6) {
        const slice = missing.slice(index, index + 6);
        const read = await Promise.all(
          slice.map(async (key) => {
            const [type, id] = key.split(':') as [string, string];
            try {
              return [key, (await client.getObject(type, id)).data] as const;
            } catch {
              return [key, null] as const;
            }
          }),
        );
        for (const [key, value] of read) found.set(key, value);
      }
      if (live) setRecords((current) => new Map([...current, ...found]));
    })();
    return () => {
      live = false;
    };
    // `records` n'est lu que pour ne pas relire : il ne relance pas la lecture.
  }, [client, wanted]);
  return records;
}

/**
 * Icône d'un type : celle que le modèle déclare (`icon`, object-definition 1.9.0, ADR-129), sinon celle d'un
 * type du Core ci-dessous, sinon l'icône générique ; la couleur distingue toujours le type.
 */
const CORE_ICONS: Readonly<Record<string, string>> = {
  Lot: 'layers',
  Shipment: 'truck',
  Party: 'users',
  Location: 'location',
  Site: 'building',
  Establishment: 'building',
  Product: 'package',
  StockMovement: 'move',
  StockBalance: 'scale',
  HandlingUnit: 'package',
  Deviation: 'alert',
  CorrectiveAction: 'check',
  Document: 'file',
  File: 'file',
  Evidence: 'clipboard',
  Recall: 'alert',
  Invoice: 'receipt',
  Settlement: 'receipt',
  Identifier: 'tag',
  Equipment: 'gauge',
  WorkItem: 'inbox',
  Case: 'inbox',
};

export interface TypeVisual {
  icon: string;
  /** Teinte déterministe 1 à 8 (jetons `--ay-avatar-<n>`) : un type garde sa couleur partout. */
  tone: number;
}

export function typeVisual(objectType: string, declared?: { icon?: string } | null): TypeVisual {
  return { icon: declared?.icon ?? CORE_ICONS[objectType] ?? 'database', tone: avatarTone(objectType) };
}

export function typeLabel(summary: ObjectSummary | null | undefined, objectType: string, plural = false): string {
  if (summary === null || summary === undefined) return objectType;
  return text(plural ? (summary.plural_label ?? summary.label) : summary.label, 'fr', objectType);
}

/** Libellé d'une relation : celui que déclare le type source, sinon l'identifiant lisible. */
export function relationLabel(summaries: ReadonlyMap<string, ObjectSummary | null>, edge: Pick<WireEdge, 'relation_id' | 'source' | 'attributes'>): string {
  if (edge.attributes?.['equivalence'] === 'same_as') return 'même tiers';
  const declared = summaries.get(edge.source.type)?.relations?.find((relation) => relation.relation_id === edge.relation_id);
  return declared?.label === undefined ? humanize(edge.relation_id) : text(declared.label, 'fr', humanize(edge.relation_id));
}

/** Libellé d'une relation par son seul identifiant : celui du premier type lu qui la déclare. */
export function relationLabelById(summaries: ReadonlyMap<string, ObjectSummary | null>, relationId: string): string {
  for (const summary of summaries.values()) {
    const declared = summary?.relations?.find((relation) => relation.relation_id === relationId);
    if (declared?.label !== undefined) return text(declared.label, 'fr', humanize(relationId));
  }
  return humanize(relationId);
}

const CODE_FIELDS =['code', 'reference', 'lot_code', 'number', 'external_reference'];
const SKIPPED_TYPES = new Set(['json', 'collection', 'file', 'rich_text', 'reference']);
const CODE_VALUE = /^[a-z][a-z0-9]*(_[a-z0-9]+)*$/;

export interface NodeFacts {
  /** Titre affiché : le libellé de la traversée, sinon le code, sinon un identifiant court. */
  title: string;
  code?: string;
  status?: { value: string; label: string };
  attributes: Array<{ label: string; value: string }>;
}

/** Ce qu'une carte montre d'un objet lu : code, statut et quelques attributs non vides. */
export function nodeFacts(summary: ObjectSummary | null | undefined, record: NodeRecord | undefined, label: string | undefined, id: string, limit: number): NodeFacts {
  const facts: NodeFacts = { title: label ?? `…${id.slice(-6)}`, attributes: [] };
  if (record === null || record === undefined) return facts;
  const fields = summary?.fields ?? [];
  const codeField = summary?.business_keys?.find((key) => typeof record[key] === 'string') ?? CODE_FIELDS.find((key) => typeof record[key] === 'string');
  if (codeField !== undefined) facts.code = String(record[codeField]);
  if (label === undefined && facts.code !== undefined) facts.title = facts.code;
  const stateField = summary?.state_field ?? 'status';
  const state = record[stateField];
  if (typeof state === 'string') {
    const field = fields.find((candidate) => candidate.field_id === stateField);
    facts.status = { value: state, label: field === undefined ? state : formatValue(state, field) };
  }
  const shown = new Set([codeField, stateField, 'display_name', 'name', 'title', 'label']);
  for (const field of fields as readonly FieldSummary[]) {
    if (facts.attributes.length >= limit) break;
    if (shown.has(field.field_id) || SKIPPED_TYPES.has(field.type)) continue;
    const value = record[field.field_id];
    if (value === undefined || value === null || value === '' || (typeof value === 'object' && !('value' in (value as object)))) continue;
    // Un code sans libellé déclaré (texte en identifiant : « supplier ») ne se montre pas sur une carte : il ne se traduit pas.
    if (field.type === 'string' && typeof value === 'string' && CODE_VALUE.test(value)) continue;
    facts.attributes.push({ label: text(field.label, 'fr', field.field_id), value: formatValue(value, field) });
  }
  return facts;
}
